import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OrderMailPreview, OrderMailTemplate } from '@kontor/shared';
import { ApiError } from '../api';
import { operatorApi } from './operatorApi';

type Loaded = OrderMailTemplate & { placeholders: { key: string; label: string; example: string }[]; defaults: { subject: string; html: string } };

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

/**
 * Mailvorlage der Bestellbestätigung (KON-206). Der Betreiber gestaltet Betreff und Text mit HTML und Platzhaltern. Die Vorschau zeigt die fertige Mail mit Beispieldaten, Logo und
 * den festen Pflichtangaben darunter, in einem abgeschotteten Rahmen (sandbox), damit eigenes HTML nie auf die Seite wirkt.
 */
export function OperatorMailTemplatePage() {
  const client = useQueryClient();
  const query = useQuery({ queryKey: ['operator-mail-template'], queryFn: async () => (await operatorApi<{ data: Loaded }>('GET', '/mail-templates/order-confirmation')).data });
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [preview, setPreview] = useState<OrderMailPreview | null>(null);
  const [testTo, setTestTo] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const loadedAt = useRef<string | null>(null);

  // Den Editor füllen, wenn die gespeicherte Vorlage ankommt oder sich ändert
  useEffect(() => {
    const d = query.data;
    if (!d) return;
    const stamp = `${d.custom}:${d.updatedAt}`;
    if (loadedAt.current === stamp) return;
    loadedAt.current = stamp;
    setSubject(d.subject);
    setHtml(d.html);
  }, [query.data]);

  const dirty = !!query.data && (subject !== query.data.subject || html !== query.data.html);

  const render = useMutation({
    mutationFn: async () => (await operatorApi<{ data: OrderMailPreview }>('POST', '/mail-templates/order-confirmation/preview', { subject, html })).data,
    onSuccess: (p) => {
      setPreview(p);
      setMessage(null);
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Die Vorschau hat nicht geklappt.') }),
  });
  // Die Vorschau folgt dem Text mit kurzer Verzögerung. Ungültiges (zum Beispiel ein unbekannter Platzhalter) meldet sich dabei sofort.
  useEffect(() => {
    if (!query.data) return;
    const t = setTimeout(() => render.mutate(), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, html, query.data?.hasLogo]);

  const save = useMutation({
    mutationFn: () => operatorApi('PUT', '/mail-templates/order-confirmation', { subject, html }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['operator-mail-template'] });
      setMessage({ ok: true, text: 'Die Vorlage ist gespeichert. Sie gilt ab der nächsten Bestellung.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Speichern hat nicht geklappt.') }),
  });
  const reset = useMutation({
    mutationFn: () => operatorApi('DELETE', '/mail-templates/order-confirmation'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['operator-mail-template'] });
      setMessage({ ok: true, text: 'Die Standardvorlage gilt wieder.' });
    },
    onError: () => setMessage({ ok: false, text: 'Das Zurücksetzen hat nicht geklappt.' }),
  });
  const test = useMutation({
    mutationFn: () => operatorApi('POST', '/mail-templates/order-confirmation/test', { to: testTo, subject, html }),
    onSuccess: () => setMessage({ ok: true, text: `Die Testmail an ${testTo} ist unterwegs. Sie zeigt den Stand aus dem Editor mit Beispieldaten.` }),
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Die Testmail ließ sich nicht senden.') }),
  });

  /** Fügt einen Platzhalter an der Stelle des Cursors in den Text ein */
  function insert(key: string) {
    const el = area.current;
    const token = `{{${key}}}`;
    if (!el) return setHtml((h) => h + token);
    const start = el.selectionStart;
    const end = el.selectionEnd;
    setHtml((h) => h.slice(0, start) + token + h.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  }

  if (query.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!query.data) return <div className="alert alert--error" role="alert">Die Vorlage ließ sich nicht laden.</div>;
  const d = query.data;

  return (
    <>
      <p><Link to="/betreiber" className="back-link">← Alle Mandanten</Link></p>
      <div className="page-head">
        <h1>Mailvorlage der Bestellbestätigung</h1>
        <span className={`badge ${d.custom ? 'badge--won' : 'badge--in_progress'}`}>{d.custom ? 'Eigene Vorlage' : 'Standardvorlage'}</span>
      </div>
      <p className="muted">
        Diese Mail bekommt ein Kunde, der auf der Webseite bestellt und bezahlt hat. Die Rechnung hängt als PDF mit E-Rechnung und als XML an. Unter Ihrem Text stehen immer die
        Pflichtangaben zu Laufzeit, Widerruf, bestätigten Bedingungen und Datenschutz, sie lassen sich hier nicht ändern.
      </p>
      {!d.hasLogo && <div className="alert alert--warn" role="status">Es ist noch kein Logo hinterlegt. Laden Sie es im Mandanten Wirkia unter Einrichtung → Firmendaten hoch, bis dahin steht der Firmenname im Kopf.</div>}
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}

      <div className="mailtpl">
        <section className="card mailtpl__editor">
          <label className="field">
            <span>Betreff</span>
            <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} />
          </label>
          <label className="field">
            <span>Text (HTML)</span>
            <textarea ref={area} className="input textarea mailtpl__html" value={html} onChange={(e) => setHtml(e.target.value)} spellCheck={false} rows={22} />
          </label>
          <div className="mailtpl__chips" role="group" aria-label="Platzhalter einfügen">
            <span className="muted small">Platzhalter einfügen:</span>
            {d.placeholders.map((p) => (
              <button key={p.key} type="button" className="chip" title={`${p.label}, zum Beispiel: ${p.example}`} onClick={() => insert(p.key)}>{`{{${p.key}}}`}</button>
            ))}
          </div>
          <div className="form-actions">
            <button className="btn btn--primary" type="button" disabled={save.isPending || !dirty} onClick={() => save.mutate()}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
            <button className="btn btn--secondary" type="button" disabled={reset.isPending || (!d.custom && !dirty)} onClick={() => (d.custom ? reset.mutate() : (setSubject(d.defaults.subject), setHtml(d.defaults.html)))}>Standardvorlage wiederherstellen</button>
          </div>
          <div className="mailtpl__test">
            <label className="field">
              <span>Testmail an</span>
              <input className="input" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="ihre@adresse.de" />
            </label>
            <button className="btn btn--secondary" type="button" disabled={test.isPending || !testTo} onClick={() => test.mutate()}>{test.isPending ? 'Wird gesendet …' : 'Testmail senden'}</button>
          </div>
        </section>

        <section className="card mailtpl__preview" aria-label="Vorschau">
          <h2>Vorschau</h2>
          <p className="muted small">Beispieldaten, so sieht der Kunde die Mail. Betreff: <strong>{preview?.subject ?? subject}</strong></p>
          {/* Abgeschotteter Rahmen: kein Skript, kein Zugriff auf diese Seite, auch wenn die Vorlage etwas Unerwartetes enthält */}
          <iframe title="Vorschau der Mail" className="mailtpl__frame" sandbox="" srcDoc={preview?.html ?? ''} />
        </section>
      </div>
    </>
  );
}
